import React, {ReactElement} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {IValidationErrorObject} from '../../../../incase-domain/common/validationErrorResponse';
import {Option} from '../../../../ui/components/common/Select';
import {selectedPresentationSelector} from '../../../../store/selectors/presentation.selectors';
import {IQuestionReportElement} from '../../../../incase-domain/elements/question/questionReportElement';
import {IElement} from '../../../../incase-domain/elements';
import {generateErrorMessages, getErrorClasses} from '../../../../utils/validation';
import ElementShadow from '../common/elementShadow';
import FontSelect from '../common/fontSelect';
import {TABS} from '../../form/part/tabs';
import SectionTitle from '../../../elements/common/SectionTitle';
import Label from '../../../../incase-domain/components/label/Label';
import DropdownMenuCompact from '../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import Button from '../../../../incase-domain/components/button/Button';
import {getQuestions, getReportSourceLabel, isWordCloudSource, wordCloudReportLayout} from './utils';
import WordCloudPaletteSelect from './WordCloudPaletteSelect';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';

interface HighlightedQuestionReportElementComponentProps {
    errors: IValidationErrorObject | null;
    activeTab: string;
}

function HighlightedQuestionReportElementComponent(props: HighlightedQuestionReportElementComponentProps): ReactElement {
    const {errors, activeTab} = props;
    const highlightedElement = useHighlightedElement() as IQuestionReportElement;
    const {questionElementId} = highlightedElement;
    const {t} = useTranslation();
    const updateHighlightedElement = useSetHighlightedElement();
    const presentation = useSelector(selectedPresentationSelector);

    const questions = getQuestions(presentation);

    const selectedQuestion = questions.find((q) => q.id === questionElementId);

    function mapToOption(source: IElement): Option {
        return {label: getReportSourceLabel(source), value: source.id};
    }

    if (activeTab === TABS.STYLE.id) {
        return <ElementShadow highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />;
    }

    if (activeTab === TABS.ADVANCED.id) {
        return <></>;
    }

    const chooseQuestionItems = [
        questions.map((el) => ({
            label: getReportSourceLabel(el),
            onClick: () => {
                if (!isWordCloudSource(el)) {
                    updateHighlightedElement.single('questionElementId', el.id);
                    return;
                }

                const layout = wordCloudReportLayout(highlightedElement.height);
                updateHighlightedElement.multiple(
                    ['questionElementId', 'fitContent', ...(layout.width ? ['width', 'height'] : [])],
                    [el.id, false, ...(layout.width ? [layout.width, layout.height] : [])]
                );
            },
        })),
    ];

    return (
        <>
            <div className="l-group--form">
                <div className={`l-group__item ${getErrorClasses(errors, 'questionElementId')}`}>
                    <Label>{t('designSidepanel.chooseQuestion')}</Label>
                    <DropdownMenuCompact
                        width="full"
                        key={selectedQuestion && getReportSourceLabel(selectedQuestion)}
                        className="c-sidepanel__dropdown"
                        items={chooseQuestionItems}
                    >
                        <Button
                            fullWidth
                            size="xs"
                            trailing="solar:alt-arrow-down-linear"
                            variant="soft"
                            className="c-sidepanel__dropdown_button"
                        >
                            {(selectedQuestion && mapToOption(selectedQuestion))?.label || t('designSidepanel.chooseQuestion')}
                        </Button>
                    </DropdownMenuCompact>

                    {generateErrorMessages(errors, 'questionElementId')}
                </div>
            </div>

            <div className="l-group--form">
                <SectionTitle label={t('designSidepanel.style')} />
                <div className="u-flex u-flex-col u-gap-2">
                    <FontSelect highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />
                    {isWordCloudSource(selectedQuestion) && (
                        <WordCloudPaletteSelect
                            value={highlightedElement.wordCloudPalette}
                            onChange={(palette) => updateHighlightedElement.single('wordCloudPalette', palette)}
                        />
                    )}
                </div>
            </div>
        </>
    );
}

export default HighlightedQuestionReportElementComponent;
